import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Loader2, CheckCircle2, Clock, MapPin, User, Mail, Phone, Plane, CreditCard, Banknote, Briefcase, Users, MessageSquare, ArrowLeftRight, Droplets, Wifi, HelpCircle } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import RouteMap from './RouteMap';
import PlacesInput from './PlacesInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingForm = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [price, setPrice] = useState(searchParams.get('price') || null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [durationText, setDurationText] = useState('');
  const [date, setDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);

  const [formData, setFormData] = useState({
    service_type: searchParams.get('service') || 'standard_premium',
    booking_type: searchParams.get('type') || 'transfer',
    route_id: searchParams.get('route') || '',
    custom_distance: '',
    hours: parseInt(searchParams.get('hours')) || 4,
    passengers: parseInt(searchParams.get('passengers')) || 1,
    pickup_time: '',
    pickup_location: '',
    dropoff_location: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    flight_number: '',
    special_requests: '',
    payment_method: 'card',
    // New fields
    trip_type: 'oneway',
    return_time: '',
    luggage_small: 0,
    luggage_large: 0,
    additional_notes: '',
  });

  const [hourlyOutsideZurich, setHourlyOutsideZurich] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [checkoutId, setCheckoutId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  const [cardWidgetReady, setCardWidgetReady] = useState(false);
  const [mountingWidget, setMountingWidget] = useState(false);
  const [sumupCardRef, setSumupCardRef] = useState(null);

  const isZurichArea = (location) => {
    const lower = location.toLowerCase();
    return lower.includes('zurich') || lower.includes('zürich') || lower.includes('zurigo') || lower.includes('kloten') || lower.includes('winterthur') || lower.includes('dietikon') || lower.includes('dübendorf') || lower.includes('opfikon') || lower.includes('wallisellen') || lower.includes('uster');
  };

  // Mount SumUp card widget when card is selected and price is available
  const mountCardWidget = async () => {
    if (!price || price <= 0 || formData.payment_method !== 'card') return;
    
    setMountingWidget(true);
    setCardWidgetReady(false);
    setPaymentStatus(null);
    
    try {
      const checkoutRes = await axios.post(`${API}/create-checkout`, {
        amount: parseFloat(price),
        description: `Transfer: ${formData.pickup_location} → ${formData.dropoff_location}`,
        booking_id: `booking-${Date.now()}`,
        currency: 'CHF',
      });
      
      setCheckoutId(checkoutRes.data.checkout_id);
      
      // Wait for DOM element to exist, retry a few times
      const tryMount = (attempt = 0) => {
        const widgetEl = document.getElementById('sumup-card-inline');
        if (widgetEl && window.SumUpCard) {
          widgetEl.innerHTML = '';
          
          try {
            const cardInstance = window.SumUpCard.mount({
              id: 'sumup-card-inline',
              checkoutId: checkoutRes.data.checkout_id,
              showSubmitButton: false,
              locale: language === 'de' ? 'de-DE' : language === 'it' ? 'it-IT' : 'en-GB',
              onResponse: async function(type, body) {
                console.log('SumUp onResponse type:', type, 'body:', JSON.stringify(body));
                
                const status = body && body.status;
                
                // Always check body.status regardless of type
                if (status === 'PAID' || status === 'SUCCESSFUL') {
                  // Double-verify on backend
                  try {
                    const verifyRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout-status/${checkoutRes.data.checkout_id}`);
                    const verifyData = await verifyRes.json();
                    if (verifyData.status === 'PAID') {
                      setPaymentStatus('success');
                      setCardWidgetReady(false);
                    } else {
                      setPaymentStatus('error');
                    }
                  } catch (verifyErr) {
                    // Backend verification failed - DO NOT trust frontend, treat as error
                    console.error('Backend payment verification failed:', verifyErr);
                    setPaymentStatus('error');
                  }
                } else if (status === 'FAILED') {
                  setPaymentStatus('error');
                } else if (status === 'PENDING') {
                  setPaymentStatus('processing');
                } else if (status === 'CANCELLED') {
                  // User cancelled - reset to allow retry
                  setPaymentStatus(null);
                } else {
                  // Unknown or no status - don't change state
                  console.log('SumUp: unhandled status', status);
                }
              },
              onLoad: function() {
                setCardWidgetReady(true);
                setMountingWidget(false);
              }
            });
            setSumupCardRef(cardInstance);
          } catch (mountErr) {
            console.error('SumUp mount error:', mountErr);
            setMountingWidget(false);
          }
          
          // Fallback: if onLoad never fires, mark as ready after 3s
          setTimeout(() => {
            setMountingWidget(false);
            setCardWidgetReady(true);
          }, 3000);
        } else if (attempt < 5) {
          setTimeout(() => tryMount(attempt + 1), 500);
        } else {
          setMountingWidget(false);
        }
      };
      
      setTimeout(() => tryMount(), 300);
    } catch (error) {
      console.error('Failed to create checkout:', error);
      setMountingWidget(false);
    }
  };

  // Don't auto-mount widget. Only reset state when payment method or price changes.
  useEffect(() => {
    if (formData.payment_method === 'card' && price && price > 0 && !hourlyOutsideZurich) {
      // Reset previous widget/checkout when price changes
      setCardWidgetReady(false);
      setCheckoutId(null);
      setPaymentStatus(null);
      setSumupCardRef(null);
      const widgetEl = document.getElementById('sumup-card-inline');
      if (widgetEl) widgetEl.innerHTML = '';
    } else {
      setCardWidgetReady(false);
      setCheckoutId(null);
    }
  }, [formData.payment_method, price]);

  useEffect(() => {
    fetchRoutes();
  }, []);

  useEffect(() => {
    if (formData.route_id && routes.length > 0) {
      const route = routes.find(r => r.id === formData.route_id);
      if (route) {
        const names = language === 'de' ? route.name_de : language === 'ro' ? route.name_ro : route.name_en;
        const [pickup, dropoff] = names.split(' - ');
        setFormData(prev => ({
          ...prev,
          pickup_location: pickup || '',
          dropoff_location: dropoff || '',
        }));
      }
    }
  }, [formData.route_id, routes, language]);

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(`${API}/routes`);
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    }
  };

  // Auto-calculate distance when both addresses are filled (for transfer type)
  useEffect(() => {
    if (formData.booking_type === 'transfer' && formData.pickup_location.length > 3 && formData.dropoff_location.length > 3) {
      const timer = setTimeout(() => {
        calculateDistanceFromAddresses();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [formData.pickup_location, formData.dropoff_location, formData.booking_type]);

  const calculateDistanceFromAddresses = async () => {
    setDistanceLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-distance`, {
        origin: formData.pickup_location,
        destination: formData.dropoff_location,
      });
      const km = response.data.distance_km;
      setFormData(prev => ({ ...prev, custom_distance: String(Math.round(km)) }));
      setDurationText(response.data.duration_text);
    } catch (error) {
      console.error('Failed to calculate distance:', error);
    } finally {
      setDistanceLoading(false);
    }
  };

  const getRouteName = (route) => {
    switch (language) {
      case 'de': return route.name_de;
      case 'ro': return route.name_ro;
      default: return route.name_en;
    }
  };

  const calculatePrice = async () => {
    try {
      const payload = {
        service_type: formData.service_type,
        booking_type: formData.booking_type,
        route_id: formData.route_id || null,
        custom_distance: formData.custom_distance ? parseFloat(formData.custom_distance) : null,
        hours: formData.booking_type === 'hourly' ? formData.hours : null,
        passengers: formData.passengers,
      };

      const response = await axios.post(`${API}/calculate-price`, payload);
      // Double price for round trip
      let calculatedPrice = response.data.price;
      if (formData.trip_type === 'roundtrip') {
        calculatedPrice = calculatedPrice * 2;
      }
      setPrice(calculatedPrice);
    } catch (error) {
      console.error('Failed to calculate price:', error);
    }
  };

  // Check Zurich area for hourly bookings (both pickup and destination must be in Zurich)
  useEffect(() => {
    if (formData.booking_type === 'hourly') {
      const pickupFilled = formData.pickup_location.length > 3;
      const dropoffFilled = formData.dropoff_location.length > 3;
      if (pickupFilled || dropoffFilled) {
        const pickupOutside = pickupFilled && !isZurichArea(formData.pickup_location);
        const dropoffOutside = dropoffFilled && !isZurichArea(formData.dropoff_location);
        const outside = pickupOutside || dropoffOutside;
        setHourlyOutsideZurich(outside);
        if (outside) {
          setPrice(null);
        }
      } else {
        setHourlyOutsideZurich(false);
      }
    } else {
      setHourlyOutsideZurich(false);
    }
  }, [formData.booking_type, formData.pickup_location, formData.dropoff_location]);

  useEffect(() => {
    if (hourlyOutsideZurich) return;
    if (formData.route_id || formData.booking_type === 'hourly' || (formData.booking_type === 'transfer' && formData.custom_distance)) {
      calculatePrice();
    }
  }, [formData.service_type, formData.booking_type, formData.route_id, formData.hours, formData.trip_type, formData.custom_distance, hourlyOutsideZurich]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Block form submission for card payment if payment hasn't been verified
    if (formData.payment_method === 'card' && paymentStatus !== 'success') {
      return;
    }
    
    if (!date || !formData.pickup_time || !formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      return;
    }

    if (formData.trip_type === 'roundtrip' && (!returnDate || !formData.return_time)) {
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        ...formData,
        pickup_date: format(date, 'yyyy-MM-dd'),
        return_date: returnDate ? format(returnDate, 'yyyy-MM-dd') : null,
        calculated_price: price || 0,
      };

      if (formData.payment_method === 'card') {
        // STRICT: Only allow booking if payment was verified as successful
        if (paymentStatus !== 'success') {
          setLoading(false);
          return;
        }
        // Double-verify payment before creating booking - MUST pass
        if (checkoutId) {
          try {
            const verifyRes = await axios.get(`${API}/checkout-status/${checkoutId}`);
            if (verifyRes.data.status !== 'PAID') {
              setPaymentStatus('error');
              setLoading(false);
              return;
            }
          } catch (verifyErr) {
            // Verification failed - DO NOT proceed with booking
            console.error('Payment verification failed:', verifyErr);
            setPaymentStatus('error');
            setLoading(false);
            return;
          }
        } else {
          // No checkout ID means no payment was made
          setPaymentStatus('error');
          setLoading(false);
          return;
        }
        bookingData.payment_status = 'paid';
        bookingData.checkout_id = checkoutId;
      }

      await axios.post(`${API}/bookings`, bookingData);
      setSuccess(true);
    } catch (error) {
      console.error('Failed to create booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRoutes = routes.filter(route => route.is_airport && route.id !== 'custom');

  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      timeSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#0A0A0A]" data-testid="booking-success">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-[#121212] border border-[#D4AF37]/30 p-12">
            <CheckCircle2 className="w-20 h-20 text-[#D4AF37] mx-auto mb-6" />
            <h2 
              className="text-3xl text-white mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t('booking_success')}
            </h2>
            <p className="text-[#A1A1AA] mb-8">
              {t('booking_success_msg')}
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-[#D4AF37] text-black font-semibold hover:bg-[#C5A059] transition-all duration-300"
              data-testid="back-home-button"
            >
              {t('nav_home')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0A0A0A]" data-testid="booking-form-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">
            Reservation
          </p>
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-white"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="booking-title"
          >
            {t('booking_title')}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Service Selection */}
          <div className="bg-[#121212] border border-white/10 p-8">
            <h3 className="text-xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Transfer Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Service Type */}
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  {t('calc_service_type')}
                </label>
                <Select
                  value={formData.service_type}
                  onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                >
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="booking-service-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="standard_premium" className="text-white">
                      {t('standard_premium')}
                    </SelectItem>
                    <SelectItem value="business" className="text-white">
                      {t('business')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Booking Type */}
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  {t('calc_booking_type')}
                </label>
                <Select
                  value={formData.booking_type}
                  onValueChange={(value) => setFormData({ ...formData, booking_type: value })}
                >
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="booking-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="transfer" className="text-white">
                      {t('calc_airport_transfer')}
                    </SelectItem>
                    <SelectItem value="hourly" className="text-white">
                      {t('calc_hourly_booking')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* From - Pickup Location */}
              <PlacesInput
                value={formData.pickup_location}
                onChange={(val) => setFormData({ ...formData, pickup_location: val })}
                placeholder={formData.booking_type === 'hourly' ? (t('hourly_location_placeholder') || 'e.g. Zurich, Kloten, Winterthur...') : t('route_from_placeholder')}
                label={formData.booking_type === 'hourly' ? `${t('hourly_location_label') || 'Pickup Area'} *` : `${t('route_from')} *`}
                required
                testId="booking-pickup"
              />

              {/* To - Destination */}
              <PlacesInput
                value={formData.dropoff_location}
                onChange={(val) => setFormData({ ...formData, dropoff_location: val })}
                placeholder={formData.booking_type === 'hourly' ? (t('hourly_destination_placeholder') || 'e.g. Zurich, Kloten, Winterthur...') : t('route_to_placeholder')}
                label={formData.booking_type === 'hourly' ? `${t('hourly_destination_label') || 'Destination Area'} *` : `${t('route_to')} *`}
                required
                testId="booking-dropoff"
              />

              {/* Distance auto-calculated */}
              {formData.booking_type === 'transfer' && (formData.custom_distance || distanceLoading) && (
                <div className="md:col-span-2 flex items-center gap-3 px-1">
                  {distanceLoading ? (
                    <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                      <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
                      <span>{t('calc_calculating')}...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-[#D4AF37] font-medium">{formData.custom_distance} km</span>
                      {durationText && <span className="text-[#A1A1AA]">~{durationText}</span>}
                    </div>
                  )}
                </div>
              )}

              {/* Hours for hourly booking */}
              {formData.booking_type === 'hourly' && (
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                    {t('calc_hours')}
                    <span className="relative group">
                      <HelpCircle className="w-4 h-4 text-[#D4AF37] cursor-help" />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#1a1a1a] border border-[#D4AF37]/30 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                        {t('calc_hours_min_tooltip')}
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    min="4"
                    max="12"
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: Math.max(4, parseInt(e.target.value) || 4) })}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                    data-testid="booking-hours"
                  />
                </div>
              )}
            </div>

            {/* Hourly - Outside Zurich Message */}
            {formData.booking_type === 'hourly' && hourlyOutsideZurich && (
              <div className="mt-6 p-6 bg-[#0A0A0A] border border-[#D4AF37]/30 text-center" data-testid="booking-hourly-zurich-message">
                <p className="text-[#D4AF37] text-lg mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('hourly_zurich_only_title') || 'Hourly Booking - Zurich Area Only'}
                </p>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  {t('hourly_zurich_only_msg') || 'Hourly booking is available only in the Zurich area. For special routes, please contact us:'}
                </p>
                <a
                  href="https://wa.me/41799270731"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-medium text-sm uppercase tracking-wider hover:bg-[#25D366]/90 transition-colors"
                  data-testid="booking-hourly-whatsapp-link"
                >
                  WhatsApp: +41 79 927 07 31
                </a>
                <p className="text-[#A1A1AA] text-xs mt-3">
                  {t('hourly_zurich_only_call') || 'Or call us at'} +41 79 927 07 31
                </p>
              </div>
            )}

            {/* Route Map */}
            <RouteMap origin={formData.pickup_location} destination={formData.dropoff_location} />
          </div>

          {/* Trip Type - One Way / Round Trip */}
          <div className="bg-[#121212] border border-white/10 p-8">
            <h3 className="text-xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <ArrowLeftRight className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
              {t('trip_type')}
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* One Way */}
              <button
                type="button"
                onClick={() => setFormData({ ...formData, trip_type: 'oneway' })}
                className={`p-4 border ${formData.trip_type === 'oneway' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#0A0A0A]'} transition-all duration-300 text-center`}
                data-testid="trip-oneway"
              >
                <p className={`font-medium ${formData.trip_type === 'oneway' ? 'text-[#D4AF37]' : 'text-white'}`}>
                  {t('trip_oneway')}
                </p>
              </button>

              {/* Round Trip */}
              <button
                type="button"
                onClick={() => setFormData({ ...formData, trip_type: 'roundtrip' })}
                className={`p-4 border ${formData.trip_type === 'roundtrip' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#0A0A0A]'} transition-all duration-300 text-center`}
                data-testid="trip-roundtrip"
              >
                <p className={`font-medium ${formData.trip_type === 'roundtrip' ? 'text-[#D4AF37]' : 'text-white'}`}>
                  {t('trip_roundtrip')}
                </p>
              </button>
            </div>

            {/* Departure Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  {t('booking_pickup_date')} *
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "w-full flex items-center justify-between bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 hover:border-[#D4AF37]/50 transition-colors",
                        !date && "text-[#A1A1AA]"
                      )}
                      data-testid="booking-date"
                    >
                      {date ? format(date, 'PPP') : t('select')}
                      <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className="bg-[#121212]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  {t('booking_pickup_time')} *
                </label>
                <Select
                  value={formData.pickup_time}
                  onValueChange={(value) => setFormData({ ...formData, pickup_time: value })}
                >
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="booking-time">
                    <SelectValue placeholder={t('select')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 max-h-64">
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time} className="text-white">
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Return Date & Time (only if round trip) */}
            {formData.trip_type === 'roundtrip' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-white/10">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    {t('trip_return_date')} *
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          "w-full flex items-center justify-between bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 hover:border-[#D4AF37]/50 transition-colors",
                          !returnDate && "text-[#A1A1AA]"
                        )}
                        data-testid="booking-return-date"
                      >
                        {returnDate ? format(returnDate, 'PPP') : t('select')}
                        <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
                      <Calendar
                        mode="single"
                        selected={returnDate}
                        onSelect={setReturnDate}
                        disabled={(d) => d < (date || new Date())}
                        initialFocus
                        className="bg-[#121212]"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    {t('trip_return_time')} *
                  </label>
                  <Select
                    value={formData.return_time}
                    onValueChange={(value) => setFormData({ ...formData, return_time: value })}
                  >
                    <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="booking-return-time">
                      <SelectValue placeholder={t('select')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 max-h-64">
                      {timeSlots.map((time) => (
                        <SelectItem key={time} value={time} className="text-white">
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Passengers & Luggage */}
          <div className="bg-[#121212] border border-white/10 p-8">
            <h3 className="text-xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <Users className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
              {t('calc_passengers')} & {t('luggage_title')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Passengers */}
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  <Users className="w-4 h-4 inline mr-1" />
                  {t('calc_passengers')}
                </label>
                <Select
                  value={String(formData.passengers)}
                  onValueChange={(value) => setFormData({ ...formData, passengers: parseInt(value) })}
                >
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="booking-passengers">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <SelectItem key={num} value={String(num)} className="text-white">
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Small Luggage */}
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  {t('luggage_small')}
                </label>
                <Select
                  value={String(formData.luggage_small)}
                  onValueChange={(value) => setFormData({ ...formData, luggage_small: parseInt(value) })}
                >
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="booking-luggage-small">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <SelectItem key={num} value={String(num)} className="text-white">
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#A1A1AA] mt-1">{t('luggage_small_desc')}</p>
              </div>

              {/* Large Luggage */}
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  {t('luggage_large')}
                </label>
                <Select
                  value={String(formData.luggage_large)}
                  onValueChange={(value) => setFormData({ ...formData, luggage_large: parseInt(value) })}
                >
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="booking-luggage-large">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <SelectItem key={num} value={String(num)} className="text-white">
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#A1A1AA] mt-1">{t('luggage_large_desc')}</p>
              </div>
            </div>
          </div>

          {/* Flight Number (for transfers) */}
          {(formData.booking_type === 'transfer' || formData.booking_type === 'airport_transfer') && (
            <div className="bg-[#121212] border border-white/10 p-8">
              <h3 className="text-xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <Plane className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
                Flight Information
              </h3>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  {t('booking_flight_number')}
                </label>
                <input
                  type="text"
                  value={formData.flight_number}
                  onChange={(e) => setFormData({ ...formData, flight_number: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="e.g. LX 1234"
                  data-testid="booking-flight"
                />
              </div>
            </div>
          )}

          {/* Customer Details */}
          <div className="bg-[#121212] border border-white/10 p-8">
            <h3 className="text-xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <User className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
              {t('booking_your_details')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  {t('booking_name')} *
                </label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                  required
                  data-testid="booking-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  {t('booking_email')} *
                </label>
                <input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                  required
                  data-testid="booking-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  {t('booking_phone')} *
                </label>
                <input
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                  required
                  data-testid="booking-phone"
                />
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-[#121212] border border-white/10 p-8">
            <h3 className="text-xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <MessageSquare className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
              {t('additional_notes')}
            </h3>
            
            <textarea
              value={formData.additional_notes}
              onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
              rows={4}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none resize-none"
              placeholder={t('additional_notes_placeholder')}
              data-testid="booking-additional-notes"
            />
          </div>

          {/* Payment Method */}
          <div className="bg-[#121212] border border-white/10 p-8">
            <h3 className="text-xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <CreditCard className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
              {t('payment_method')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Card Payment */}
              <button
                type="button"
                onClick={() => setFormData({ ...formData, payment_method: 'card' })}
                className={`p-4 border ${formData.payment_method === 'card' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#0A0A0A]'} transition-all duration-300 flex items-center gap-4`}
                data-testid="payment-card"
              >
                <div className={`w-12 h-12 flex items-center justify-center border ${formData.payment_method === 'card' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/20 text-white/60'}`}>
                  <CreditCard className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className={`font-medium ${formData.payment_method === 'card' ? 'text-[#D4AF37]' : 'text-white'}`}>
                    {t('payment_card')}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Visa, Mastercard, TWINT</p>
                </div>
              </button>

              {/* Cash Payment */}
              <button
                type="button"
                onClick={() => setFormData({ ...formData, payment_method: 'pickup' })}
                className={`p-4 border ${formData.payment_method === 'pickup' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#0A0A0A]'} transition-all duration-300 flex items-center gap-4`}
                data-testid="payment-pickup"
              >
                <div className={`w-12 h-12 flex items-center justify-center border ${formData.payment_method === 'pickup' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/20 text-white/60'}`}>
                  <Banknote className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className={`font-medium ${formData.payment_method === 'pickup' ? 'text-[#D4AF37]' : 'text-white'}`}>
                    {t('payment_pickup')}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">CHF, EUR</p>
                </div>
              </button>
            </div>
            
            {/* SumUp Card Widget - inline when card is selected */}
            {formData.payment_method === 'card' && price > 0 && !hourlyOutsideZurich && (
              <div className="mt-6" data-testid="card-widget-container">
                {/* Amount to pay */}
                <div className="text-center mb-4 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                  <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">{t('amount_to_pay') || 'Amount to Pay'}</p>
                  <p className="text-3xl text-[#D4AF37] font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="card-payment-amount">
                    CHF {parseFloat(price).toFixed(2)}
                  </p>
                </div>
                
                {/* Before widget is mounted - show "Proceed to Payment" button */}
                {!checkoutId && !mountingWidget && paymentStatus !== 'success' && (
                  <button
                    type="button"
                    onClick={mountCardWidget}
                    className="w-full px-8 py-4 bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#C5A059] transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="proceed-to-payment-btn"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    {t('proceed_to_payment') || 'Proceed to Card Payment'}
                  </button>
                )}
                
                {mountingWidget && !cardWidgetReady && (
                  <div className="flex items-center justify-center gap-3 py-6 text-[#A1A1AA]">
                    <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
                    <span className="text-sm">{t('loading_card_form') || 'Loading secure payment form...'}</span>
                  </div>
                )}
                
                {paymentStatus === 'error' && (
                  <div className="bg-red-500/10 border-2 border-red-500/50 p-6 mb-4 text-center" data-testid="payment-failed-message">
                    <svg className="w-10 h-10 text-red-400 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                    <p className="text-red-400 text-lg font-semibold mb-1">{t('payment_failed_title') || 'Payment Failed'}</p>
                    <p className="text-red-300 text-sm mb-4">{t('payment_failed_msg') || 'The payment could not be processed. Please check your card details and try again.'}</p>
                    <button
                      type="button"
                      onClick={mountCardWidget}
                      className="px-6 py-3 bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#C5A059] transition-colors"
                      data-testid="retry-payment-btn"
                    >
                      {t('retry_payment') || 'Try Again'}
                    </button>
                  </div>
                )}
                
                {paymentStatus === 'processing' && (
                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 mb-4 text-center" data-testid="payment-processing-message">
                    <Loader2 className="w-8 h-8 text-[#D4AF37] mx-auto mb-3 animate-spin" />
                    <p className="text-[#D4AF37] text-lg font-semibold mb-1">{t('payment_processing_title') || 'Processing Payment...'}</p>
                    <p className="text-[#A1A1AA] text-sm">{t('payment_processing_msg') || 'Please wait while we process your payment. Do not close this page.'}</p>
                  </div>
                )}
                
                {paymentStatus === 'success' && (
                  <div className="bg-green-500/10 border border-green-500/30 p-4 mb-4 text-center">
                    <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 text-sm">{t('payment_success') || 'Payment successful!'}</p>
                  </div>
                )}
                
                {/* SumUp widget container - only visible when mounted */}
                <div 
                  id="sumup-card-inline" 
                  className="bg-white rounded-lg overflow-hidden"
                  data-testid="sumup-card-inline"
                  style={{ display: (checkoutId && paymentStatus !== 'success' && paymentStatus !== 'error') ? 'block' : 'none', minHeight: cardWidgetReady ? 'auto' : '180px' }}
                />
                
                {/* Custom Pay Button - only after widget is ready */}
                {cardWidgetReady && paymentStatus !== 'success' && paymentStatus !== 'error' && (
                  <button
                    type="button"
                    onClick={() => { if (sumupCardRef) sumupCardRef.submit(); }}
                    className="w-full mt-4 px-8 py-4 bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#C5A059] transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="custom-pay-button"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    {`${t('confirm_and_pay') || 'Confirm & Pay'} CHF ${parseFloat(price).toFixed(2)}`}
                  </button>
                )}
                
                <p className="text-[#A1A1AA] text-xs text-center mt-3 flex items-center justify-center gap-1">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  {t('payment_secure') || 'Secure payment powered by SumUp'}
                </p>
              </div>
            )}
            
            {/* No price yet - prompt to fill route */}
            {formData.payment_method === 'card' && (!price || price <= 0) && (
              <div className="mt-4 p-4 border border-white/10 text-center">
                <p className="text-[#A1A1AA] text-sm">
                  {t('card_fill_route') || 'Please fill in the route details above to see the payment form.'}
                </p>
              </div>
            )}
          </div>

          {/* Price & Submit */}
          <div className="bg-[#121212] border border-[#D4AF37]/30 p-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              {/* Price Display (hidden when card payment selected or hourly outside Zurich) */}
              {price && !hourlyOutsideZurich && formData.payment_method !== 'card' && (
                <div className="text-center sm:text-left">
                  <p className="text-sm text-[#A1A1AA] mb-1">{t('calc_your_price')}</p>
                  <p 
                    className="text-4xl gold-text"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    data-testid="booking-price"
                  >
                    CHF {parseFloat(price).toFixed(2)}
                  </p>
                  {formData.trip_type === 'roundtrip' && (
                    <p className="text-xs text-[#D4AF37] mt-1">{t('trip_roundtrip')}</p>
                  )}
                </div>
              )}

              {/* Submit Button - Cash: always visible / Card: only after successful payment */}
              {formData.payment_method !== 'card' && (
                <button
                  type="submit"
                  disabled={loading || hourlyOutsideZurich}
                  className="w-full sm:w-auto px-10 py-4 bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-booking-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('booking_submit')
                  )}
                </button>
              )}
              
              {/* Card: Confirm Booking after successful payment */}
              {formData.payment_method === 'card' && paymentStatus === 'success' && (
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-10 py-4 bg-green-600 text-white font-semibold text-sm uppercase tracking-wider hover:bg-green-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-booking-after-payment"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      {t('confirm_booking_paid') || 'Confirm Booking'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingForm;
